import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Calendar, Clock, MapPin, Users, Mail, Phone, User,
  AlertCircle, CheckCircle, DollarSign, FileText,
  ArrowLeft, ExternalLink
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import api from '../../services/apiService';

interface TrainingDetails {
  id: number;
  type: string;
  title: string;
  description: string;
  training_type: string;
  level: string;
  category: string;
  age_group: string;
  start_date: string;
  end_date: string;
  start_time: string | null;
  end_time: string | null;
  venue: string;
  venue_address: string;
  instructor: string;
  instructor_bio: string;
  max_participants: number | null;
  registered_count: number;
  available_spots: number | null;
  fee: string | null;
  requirements: string[];
  contact_person: string;
  contact_email: string;
  contact_phone: string;
  is_registration_open: boolean;
  registration_count: number;
}

interface RegistrationForm {
  participant_name: string;
  participant_email: string;
  participant_phone: string;
  date_of_birth: string;
  guardian_name: string;
  guardian_phone: string;
  medical_conditions: string;
  emergency_contact: string;
  notes: string;
}

const TrainingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [training, setTraining] = useState<TrainingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [form, setForm] = useState<RegistrationForm>({
    participant_name: '',
    participant_email: '',
    participant_phone: '',
    date_of_birth: '',
    guardian_name: '',
    guardian_phone: '',
    medical_conditions: '',
    emergency_contact: '',
    notes: '',
  });

  const loadTrainingDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/schedule/event/training/${id}`);
      setTraining(response.data);
    } catch (error) {
      console.error('Error loading training details:', error);
      toast.error('Failed to load training details');
      navigate('/schedule');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadTrainingDetails();
    }
  }, [id]);

  const calculateAge = (birthDate: string): number => {
    if (!birthDate) return 0;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const isMinor = calculateAge(form.date_of_birth) < 18;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!training) return;

    try {
      setRegistering(true);
      
      const registrationData = {
        ...form,
        is_minor: isMinor,
      };

      await api.post(`/api/schedule/register/training/${training.id}`, registrationData);
      
      toast.success('Registration successful! You will receive a confirmation email shortly.');
      setShowRegistrationForm(false);
      setForm({
        participant_name: '',
        participant_email: '',
        participant_phone: '',
        date_of_birth: '',
        guardian_name: '',
        guardian_phone: '',
        medical_conditions: '',
        emergency_contact: '',
        notes: '',
      });
      
      // Reload training details to get updated registration count
      await loadTrainingDetails();
    } catch (error: any) {
      console.error('Registration error:', error);
      const message = error.response?.data?.error || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <>
        <div className="flex justify-center items-center min-h-screen">
          <LoadingSpinner size="large" />
        </div>
      </>
    );
  }

  if (!training) {
    return (
      <>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Training Not Found</h2>
          <button
            onClick={() => navigate('/schedule')}
            className="text-blue-600 hover:text-blue-700"
          >
            Return to Schedule
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/schedule')}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Schedule
          </button>
          
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              {training.training_type}
            </span>
            {training.level && (
              <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">
                {training.level}
              </span>
            )}
            {training.age_group && (
              <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                {training.age_group}
              </span>
            )}
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{training.title}</h1>
          <p className="text-gray-600 text-lg">{training.description}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Schedule Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Schedule & Location</h2>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <Calendar className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {new Date(training.start_date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                      {training.start_date !== training.end_date && (
                        <span>
                          {' - '}
                          {new Date(training.end_date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      )}
                    </p>
                    {training.start_time && training.end_time && (
                      <p className="text-gray-600">
                        {new Date(`2000-01-01T${training.start_time}`).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                        {' - '}
                        {new Date(`2000-01-01T${training.end_time}`).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-start">
                  <MapPin className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">{training.venue}</p>
                    {training.venue_address && (
                      <p className="text-gray-600">{training.venue_address}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Instructor Information */}
            {training.instructor && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Instructor</h2>
                
                <div className="flex items-start">
                  <User className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">{training.instructor}</p>
                    {training.instructor_bio && (
                      <p className="text-gray-600 mt-2">{training.instructor_bio}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Requirements */}
            {training.requirements && training.requirements.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Requirements</h2>
                
                <ul className="space-y-2">
                  {training.requirements.map((requirement, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{requirement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Registration Status */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Registration</h3>
              
              {training.is_registration_open ? (
                <div className="space-y-4">
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    <span className="font-medium">Registration Open</span>
                  </div>
                  
                  {training.max_participants && (
                    <div className="text-sm text-gray-600">
                      <div className="flex justify-between mb-2">
                        <span>Registered:</span>
                        <span>{training.registered_count}/{training.max_participants}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{
                            width: `${Math.min((training.registered_count / training.max_participants) * 100, 100)}%`
                          }}
                        ></div>
                      </div>
                      {training.available_spots !== null && (
                        <p className="mt-2 text-green-600">
                          {training.available_spots} spots available
                        </p>
                      )}
                    </div>
                  )}
                  
                  {training.fee && (
                    <div className="flex items-center text-gray-600">
                      <DollarSign className="w-5 h-5 mr-2" />
                      <span>Fee: ₹{training.fee}</span>
                    </div>
                  )}
                  
                  <button
                    onClick={() => setShowRegistrationForm(true)}
                    disabled={training.available_spots === 0}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                      training.available_spots === 0
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {training.available_spots === 0 ? 'Training Full' : 'Register Now'}
                  </button>
                </div>
              ) : (
                <div className="flex items-center text-red-600">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  <span className="font-medium">Registration Closed</span>
                </div>
              )}
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact</h3>
              
              <div className="space-y-3">
                {training.contact_person && (
                  <div className="flex items-center text-gray-600">
                    <User className="w-4 h-4 mr-3" />
                    <span>{training.contact_person}</span>
                  </div>
                )}
                
                {training.contact_email && (
                  <div className="flex items-center text-gray-600">
                    <Mail className="w-4 h-4 mr-3" />
                    <a
                      href={`mailto:${training.contact_email}`}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      {training.contact_email}
                    </a>
                  </div>
                )}
                
                {training.contact_phone && (
                  <div className="flex items-center text-gray-600">
                    <Phone className="w-4 h-4 mr-3" />
                    <a
                      href={`tel:${training.contact_phone}`}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      {training.contact_phone}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Registration Modal */}
        {showRegistrationForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Training Registration</h2>
                
                <form onSubmit={handleSubmitRegistration} className="space-y-4">
                  {/* Participant Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="participant_name"
                        value={form.participant_name}
                        onChange={handleInputChange}
                        required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="participant_email"
                        value={form.participant_email}
                        onChange={handleInputChange}
                        required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="participant_phone"
                        value={form.participant_phone}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date of Birth *
                      </label>
                      <input
                        type="date"
                        name="date_of_birth"
                        value={form.date_of_birth}
                        onChange={handleInputChange}
                        required
                        max={new Date().toISOString().split('T')[0]}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Guardian Information (for minors) */}
                  {isMinor && (
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h3 className="text-lg font-medium text-gray-900 mb-3">
                        Guardian Information (Required for participants under 18)
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Guardian Name *
                          </label>
                          <input
                            type="text"
                            name="guardian_name"
                            value={form.guardian_name}
                            onChange={handleInputChange}
                            required={isMinor}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Guardian Phone *
                          </label>
                          <input
                            type="tel"
                            name="guardian_phone"
                            value={form.guardian_phone}
                            onChange={handleInputChange}
                            required={isMinor}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Medical & Emergency Information */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Medical Conditions or Allergies
                    </label>
                    <textarea
                      name="medical_conditions"
                      value={form.medical_conditions}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Please list any medical conditions, allergies, or medications that coaches should be aware of"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Emergency Contact *
                    </label>
                    <textarea
                      name="emergency_contact"
                      value={form.emergency_contact}
                      onChange={handleInputChange}
                      required
                      rows={2}
                      placeholder="Name, relationship, and phone number of emergency contact"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Notes
                    </label>
                    <textarea
                      name="notes"
                      value={form.notes}
                      onChange={handleInputChange}
                      rows={2}
                      placeholder="Any additional information or special requests"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex gap-4 pt-6">
                    <button
                      type="button"
                      onClick={() => setShowRegistrationForm(false)}
                      className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={registering}
                      className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {registering ? 'Registering...' : 'Register'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TrainingDetailPage;